import React from 'react';

import {MarkdownImageProps} from '../../../components/utils/md';
import {cls, coverSize, imageUrl} from '../../../components/utils';
import {useGallery} from '../../../components/gallery';
import type {GalleryImage} from '../../../components/gallery';
import type {WikiInfobox} from '../infobox';

import css from './wiki-view.module.css';

const THUMB_WIDTH = 194;
const THUMB_HEIGHT = 150;
const IMAGE_TAG = /\[img\s[^\]]*\/\]/g;
const ATTRIBUTE = /([a-zA-Z][\w-]*)\s*=\s*"([^"]*)"/g;

function attributesOf(tag: string): MarkdownImageProps {
	const result: Record<string, string> = {};
	for (const found of tag.matchAll(ATTRIBUTE)) result[found[1]] = found[2];
	return result;
}

function toGalleryImage(
	path: string,
	width?: number,
	height?: number,
	caption?: string,
	author?: string,
): GalleryImage {
	return {
		id: path,
		url: imageUrl(path),
		preview: imageUrl(path, coverSize(width, height, THUMB_WIDTH, THUMB_HEIGHT)),
		description: caption || '',
		credits: author,
		width,
		height,
	};
}

/** картинки статьи для просмотрщика: сначала картинка карточки, потом картинки текста по порядку */
export function wikiGalleryImages(description: string, infobox?: WikiInfobox | null): GalleryImage[] {
	const images: GalleryImage[] = [];
	const image = infobox?.image;
	if (image?.path) images.push(toGalleryImage(image.path, image.width, image.height, image.caption, image.author));
	const emblem = infobox?.emblem;
	if (emblem?.path)
		images.push(toGalleryImage(emblem.path, emblem.width, emblem.height, emblem.caption || 'Герб', emblem.author));
	const locator = infobox?.locator;
	if (locator?.path)
		images.push(toGalleryImage(locator.path, locator.width, locator.height, locator.caption || 'На карте Фейруна'));
	for (const tag of description.match(IMAGE_TAG) || []) {
		const attrs = attributesOf(tag);
		if (!attrs.src || attrs.gallery === 'false' || images.some((item) => item.id === attrs.src)) continue;
		images.push(
			toGalleryImage(
				attrs.src,
				Number(attrs.width) || undefined,
				Number(attrs.height) || undefined,
				attrs.caption,
				attrs.author,
			),
		);
	}
	return images;
}

/** Картинка в тексте статьи: в ряду — миниатюра галереи, одна — фигура во всю ширину */
export const WikiImage: React.FC<{image: MarkdownImageProps}> = ({image}) => {
	const openImage = useGallery();
	const {src, caption} = image;
	if (!src) return null;
	const width = Number(image.width) || undefined;
	const height = Number(image.height) || undefined;
	const inRow = image.inRow === 'true';
	const side = !inRow && (image.side === 'left' || image.side === 'right') ? image.side : null;

	// оформление, а не иллюстрация: значок рядом с таблицей, без увеличения и галереи
	if (image.gallery === 'false') {
		return (
			<figure id={image.id} className={css.figure}>
				<img
					src={imageUrl(src, 400)}
					width={width}
					height={height}
					alt={caption || ''}
					loading="lazy"
					decoding="async"
				/>
			</figure>
		);
	}

	return (
		<figure
			id={image.id}
			className={
				inRow ? css.thumbnail : cls(css.figure, side && css.figureSide, side === 'left' && css.figureLeft)
			}
		>
			<a
				href={imageUrl(src)}
				onClick={(event) => {
					event.preventDefault();
					openImage(src);
				}}
			>
				<img
					src={
						inRow ? imageUrl(src, coverSize(width, height, THUMB_WIDTH, THUMB_HEIGHT)) : imageUrl(src, 900)
					}
					width={inRow ? THUMB_WIDTH : width}
					height={inRow ? THUMB_HEIGHT : height}
					alt={caption || ''}
					loading="lazy"
					decoding="async"
				/>
			</a>
			{caption && <figcaption className={css.caption}>{caption}</figcaption>}
		</figure>
	);
};
